// Outreach kit for the German market — phone first (K3), message only after a
// spoken yes. Pure functions: every sentence is built from fields the business
// record actually has, and a missing field removes its sentence rather than
// leaving a hole. See proposal §D for the source wording and §D7 for the
// consent/UWG §7 questions a lawyer must answer — none of this is legal advice.
import { prettyBot } from "./bots";

export const OPT_OUT = "Wenn Sie keine weiteren Nachrichten von mir möchten, schreiben Sie einfach „nein danke“.";
export const VARIANTS = ["gap", "legal", "nosite", "aiblind"];
export const VARIANT_LABELS = {
  gap: "Rakip farkı (puan + Maps sırası)",
  legal: "Yasal / görsel telif",
  nosite: "Web sitesi yok",
  aiblind: "Puan iyi ama YZ botları engelli",
};

const clean = (s) => (s == null ? "" : String(s).trim());
const num = (n) => String(n).replace(".", ",");
const join = (...parts) => parts.filter((p) => p && String(p).trim()).join("\n\n");

export function domainOf(url) {
  const u = clean(url);
  if (!u) return "";
  try {
    return new URL(/^https?:\/\//i.test(u) ? u : `https://${u}`).hostname.replace(/^www\./i, "").toLowerCase();
  } catch {
    return "";
  }
}

export function outreachFacts({ business: b, city, top3, benchmark }) {
  const issues = b.legal?.issues || [];
  const findings = b.audit_findings || [];
  const mr = b.maps_rank;
  const maps = mr && !mr.error && mr.found && mr.rank_absolute && mr.total_checked && mr.keyword
    ? { rank: mr.rank_absolute, total: mr.total_checked, keyword: mr.keyword } : null;
  // Non-checkable entries (Bing Places, Apple Maps) are excluded from the
  // denominator, same rule as the public report — "X of Y checked" must not
  // count what cannot be verified.
  const sites = (b.citations?.sites || []).filter((s) => s.checkable !== false);
  const imgIssue = issues.find((i) => i.code === "stock_images_detected");
  const t = top3?.[0];
  const top1 = t && typeof t.label === "string" && t.label.trim() && Number.isFinite(t.score) ? t : null;
  const bots = (findings.find((f) => f.code === "ai_bots_blocked")?.data?.bots || []).map(prettyBot);
  return {
    name: clean(b.name) || "Ihr Unternehmen",
    owner: clean(b.owner),
    city: clean(city),
    score: b.digital_score ?? null,
    median: benchmark?.score_median ?? null,
    cohortSize: benchmark?.cohort_size ?? null,
    withWebsitePct: benchmark?.with_website_pct ?? null,
    top1: top1 ? { label: top1.label.trim(), score: top1.score } : null,
    maps,
    citFound: sites.length ? sites.filter((s) => s.found).length : null,
    citTotal: sites.length || null,
    legalN: issues.length,
    imgN: typeof imgIssue?.data?.count === "number" ? imgIssue.data.count : null,
    hasImgFlag: !!imgIssue,
    hasCriticalLegal: issues.some((i) => i.severity === "critical"),
    bots,
    chatgptBlocked: bots.some((x) => ["gptbot", "oai-searchbot"].includes(String(x).toLowerCase())),
    noLlmsAndFaq: findings.some((f) => f.code === "no_llms_txt") && findings.some((f) => f.code === "no_faq_schema"),
    // Same rule as the public report's finding_counts: positives are not "points to fix".
    findingTotal: findings.filter((f) => ["critical", "high", "medium", "low"].includes(f.severity)).length,
    domain: domainOf(b.website),
    hasWebsite: b.has_website !== false,
    reviewCount: b.review_count ?? null,
    rating: b.rating ?? null,
  };
}

export function pickVariant(f) {
  if (!f.hasWebsite) return "nosite";
  if (f.legalN > 0 && (f.hasCriticalLegal || f.hasImgFlag)) return "legal";
  if (f.score != null && f.median != null && f.score >= f.median && (f.bots.length || f.noLlmsAndFaq)) return "aiblind";
  return "gap";
}

const reviewsPhrase = (n) => `${n} Google-${n === 1 ? "Bewertung" : "Bewertungen"}`;

// Number-correct, generic-true wording: every bot in the list is an AI crawler.
const botsBlocked = (f, lead) => {
  const one = f.bots.length === 1;
  return `${lead} robots.txt ${one ? "ist" : "sind"} ${f.bots.join(", ")} gesperrt`;
};
const botsExplain = (f) => (f.bots.length === 1
  ? "das ist ein Programm, mit dem KI-Assistenten Websites lesen"
  : "das sind Programme, mit denen KI-Assistenten Websites lesen");

const greet = (f) => (f.owner ? `Guten Tag, ${f.owner},` : "Guten Tag,");

const legalSentence = (f) => {
  const where = f.domain ? `Auf ${f.domain}` : "Auf Ihrer Website";
  return f.legalN === 1
    ? `${where} ist mir 1 Punkt aufgefallen, der in Deutschland zu Abmahnungen führen kann.`
    : `${where} sind mir ${f.legalN} Punkte aufgefallen, die in Deutschland häufig zu Abmahnungen führen.`;
};

// The one observation the caller opens the conversation with (spoken aloud).
function hookSentence(v, f) {
  switch (v) {
    case "legal":
      return `${legalSentence(f).replace(/\.$/, "")} — ein Hinweis, keine Rechtsberatung`;
    case "nosite":
      return f.reviewCount
        ? `${f.name} hat ${reviewsPhrase(f.reviewCount)}, aber keine eigene Website`
        : `${f.name} hat keine eigene Website`;
    case "aiblind":
      return f.bots.length
        ? `${botsBlocked(f, "in Ihrer")} — ${botsExplain(f)}`
        : "Ihre Website enthält keine strukturierten Frage-Antwort-Inhalte und keine Beschreibungsdatei für KI-Assistenten";
    default: // gap
      if (f.maps) return `bei der Suche „${f.maps.keyword}“ stehen Sie bei Google Maps auf Platz ${f.maps.rank} von ${f.maps.total}`;
      if (f.top1 && f.score != null) return `${f.top1.label} aus Ihrer Umgebung liegt bei ${f.top1.score} von 100 Punkten, Sie bei ${f.score}`;
      if (f.score != null) return `Ihre Online-Sichtbarkeit liegt bei ${f.score} von 100 Punkten${f.median != null ? ` (Median Ihrer Branche: ${f.median})` : ""}`;
      return null;
  }
}

export function phoneScript(v, f, sender, price) {
  const hook = hookSentence(v, f);
  const who = f.owner ? `Spreche ich mit ${f.owner}?` : `Spreche ich mit der Inhaberin oder dem Inhaber von ${f.name}?`;
  return [
    "[SÖYLE — açılış]",
    `Guten Tag, hier ist ${sender}. ${who} Haben Sie kurz eine halbe Minute?`,
    "",
    "[SÖYLE — sebep]",
    `Ich messe für Betriebe${f.city ? ` in ${f.city}` : ""}, wie gut sie online zu finden sind — bei Google, bei Google Maps und in KI-Antworten wie ChatGPT. ${hook ? `Bei ${f.name} ist mir dabei etwas aufgefallen: ${hook}. Das können Sie in zwei Minuten selbst nachprüfen.` : `Ich habe für ${f.name} einen kostenlosen Kurzbericht zur Online-Sichtbarkeit erstellt.`}`,
    "",
    "[SÖYLE — izin iste]",
    "Ich habe dazu einen kostenlosen Kurzbericht erstellt. Darf ich Ihnen den Link per WhatsApp oder E-Mail schicken?",
    "",
    "[EVET derse] Diyalogda onayı “sözlü” olarak işaretle, sonra WhatsApp/E-posta sekmesindeki mesajı gönder.",
    "[HAYIR derse — söyle] „Kein Problem, danke für Ihre Zeit. Ich melde mich nicht wieder.“ — ve gerçekten bir daha arama.",
    `[FİYAT sorarsa — söyle] „Der Kurzbericht ist kostenlos. Der ausführliche Bericht kostet ${price} einmalig — das steht auf der Seite, Sie müssen nichts kaufen.“`,
  ].join("\n");
}

// What the written message states after the call. Same facts as the hook, in
// full sentences; nothing here is a claim the report does not back up.
function factLine(v, f) {
  const bits = [];
  switch (v) {
    case "legal": {
      bits.push(legalSentence(f));
      if (f.imgN) bits.push(`Bei ${f.imgN} ${f.imgN === 1 ? "Bild" : "Bildern"} deutet der Dateiname oder die Herkunft auf eine Bildagentur hin.`);
      bits.push("Das ist ein Hinweis, keine Rechtsberatung — bitte lassen Sie es im Zweifel anwaltlich prüfen.");
      break;
    }
    case "nosite": {
      bits.push(`${f.name} hat keine eigene Website${f.reviewCount ? `, dafür aber ${reviewsPhrase(f.reviewCount)}${f.rating ? ` mit ${num(f.rating)} Sternen` : ""}` : ""}.`);
      if (f.score != null && f.cohortSize && f.city) {
        bits.push(`Im Vergleich zu ${f.cohortSize} Betrieben Ihrer Branche in ${f.city} liegen Sie bei ${f.score} von 100.`);
        if (f.withWebsitePct != null) bits.push(`${num(f.withWebsitePct)} % dieser Betriebe haben eine Website.`);
      }
      const measured = [
        f.reviewCount != null ? "Ihr Google-Unternehmensprofil und Ihre Bewertungen" : null,
        f.maps ? "die Auffindbarkeit bei Google Maps" : null,
        f.citTotal ? "Ihre Einträge in Branchenverzeichnissen" : null,
      ].filter(Boolean);
      if (measured.length) {
        const list = measured.length > 1 ? `${measured.slice(0, -1).join(", ")} und ${measured[measured.length - 1]}` : measured[0];
        bits.push(`Eine Website können wir natürlich nicht prüfen — gemessen haben wir: ${list}.`);
      }
      break;
    }
    case "aiblind": {
      const base = f.score != null && f.median != null
        ? `${f.name} liegt bei ${f.score} von 100 Punkten (Median Ihrer Branche: ${f.median}).`
        : "";
      if (f.bots.length) {
        const b = botsBlocked(f, "In Ihrer");
        const e = botsExplain(f);
        bits.push(base, `${b}. ${e[0].toUpperCase()}${e.slice(1)}.`);
        if (f.domain) bits.push(`Prüfen können Sie das in zehn Sekunden: ${f.domain}/robots.txt im Browser öffnen.`);
      } else {
        bits.push(base, "Auffällig ist etwas anderes: Ihre Website enthält keine strukturierten Frage-Antwort-Inhalte und keine Beschreibungsdatei für KI-Assistenten. Ob ChatGPT Sie heute nennt, haben wir noch nicht getestet — das können Sie im Kurzbericht mit einem Klick selbst auslösen.");
      }
      break;
    }
    default: { // gap
      if (f.score != null) bits.push(`${f.name} liegt bei ${f.score} von 100 Punkten${f.top1 ? `, ${f.top1.label} aus Ihrer Umgebung bei ${f.top1.score}` : ""}.`);
      if (f.maps) bits.push(`Bei der Suche „${f.maps.keyword}“ stehen Sie bei Google Maps auf Platz ${f.maps.rank} von ${f.maps.total}.`);
      if (f.citTotal) bits.push(`In Branchenverzeichnissen sind Sie auf ${f.citFound} von ${f.citTotal} geprüften Plattformen auffindbar.`);
    }
  }
  return bits.filter(Boolean).join(" ");
}

function subject(v, f) {
  switch (v) {
    case "legal": return `${f.name}: ${f.legalN} rechtliche ${f.legalN === 1 ? "Punkt" : "Punkte"} auf Ihrer Website (bitte prüfen lassen)`;
    case "nosite": return f.reviewCount
      ? `${f.name}${f.city ? ` in ${f.city}` : ""}: ${f.reviewCount} ${f.reviewCount === 1 ? "Bewertung" : "Bewertungen"}, aber keine eigene Website`
      : `Was Kunden über ${f.name} online finden — und was nicht`;
    case "aiblind": return f.bots.length ? (f.chatgptBlocked ? `${f.name}: ChatGPT darf Ihre Website nicht lesen` : `${f.name}: KI-Assistenten dürfen Ihre Website nicht lesen`) : `${f.name}: Wie sichtbar sind Sie für KI-Assistenten?`;
    default: return f.maps
      ? `${f.name}: Platz ${f.maps.rank} von ${f.maps.total} bei „${f.maps.keyword}“`
      : f.score != null
        ? `${f.name} bei Google: ${f.score} von 100 — gemessen, nicht geschätzt`
        : `Kurzbericht zur Online-Sichtbarkeit von ${f.name}`;
  }
}

// A requested variant is honoured only when the record backs it up.
function variantAllowed(variant, f) {
  switch (variant) {
    case "legal": return f.legalN > 0;
    case "nosite": return !f.hasWebsite;
    case "aiblind": return f.bots.length > 0 || f.noLlmsAndFaq;
    case "gap": return true;
    default: return false;
  }
}

export function buildOutreach({
  business, city, sender, top3, benchmark, reportUrl, price = "39 €", paymentLive = false, variant = null,
}) {
  const f = outreachFacts({ business, city, top3, benchmark });
  const v = variantAllowed(variant, f) ? variant : pickVariant(f);
  const g = greet(f);
  const buy = paymentLive ? "freischalten" : "anfordern";
  const facts = factLine(v, f);

  const waFirst = join(
    g,
    join(`wie eben besprochen schicke ich Ihnen den Kurzbericht zu ${f.name}.`, facts).replace("\n\n", " "),
    `Der Bericht ist kostenlos, ohne Anmeldung, nichts auszufüllen:\n${reportUrl}`,
    `Alles darin können Sie in zwei Minuten selbst nachprüfen. Der ausführliche Bericht (${price}, einmalig) ist optional — Sie müssen nichts kaufen.`,
    `— ${sender}`,
    OPT_OUT,
  );

  const emailFirst = join(
    g,
    `wie eben am Telefon besprochen sende ich Ihnen den kostenlosen Kurzbericht zu ${f.name}.`,
    facts,
    `Den Bericht finden Sie hier:\n${reportUrl}`,
    "Er zeigt Ihnen Ihre Ergebnisse im Überblick. Jede Zahl darin können Sie selbst nachprüfen.",
    `Wenn Sie den vollständigen Bericht möchten — alle Punkte mit Beweis und Lösungsschritt, plus der ausführliche ChatGPT-Test mit 12 Fragen — können Sie ihn am Ende der Seite für ${price} ${buy}. Kein Abo, kein Telefonat nötig.`,
    `Mit freundlichen Grüßen\n${sender}`,
    OPT_OUT,
  );

  const sig = `Mit freundlichen Grüßen\n${sender}`;
  const noOpenParts = [
    g,
    `kurze Erinnerung: der Kurzbericht zu ${f.name} liegt noch unter\n${reportUrl}`,
    "Er läuft nicht ab, kostet nichts und verlangt keine Anmeldung.",
  ];
  const ft = f.findingTotal;
  const pointsRef = ft === 0 ? "zu jedem gefundenen Punkt" : ft === 1 ? "zu dem einen gefundenen Punkt" : `zu jedem der ${ft} gefundenen Punkte`;
  const openedParts = [
    g,
    `Sie haben den Bericht zu ${f.name} geöffnet — danke dafür.`,
    `Falls eine Frage offen geblieben ist: im ausführlichen Bericht steht ${pointsRef}, was genau dahintersteckt und mit welchem Schritt er behoben ist — ${price} einmalig, kein Abo, kein Telefonat:\n${reportUrl}#angebot`,
    "Wenn Sie lieber selbst weitermachen: auch gut. Dann hat der Kurzbericht schon seinen Zweck erfüllt.",
  ];

  return {
    variant: v,
    facts: f,
    phone: phoneScript(v, f, sender, price),
    first: { wa: waFirst, emailSubject: subject(v, f), emailBody: emailFirst },
    followupNoOpen: { wa: join(...noOpenParts, `— ${sender}`, OPT_OUT), emailSubject: `Kurzer Nachtrag: Kurzbericht zu ${f.name}`, emailBody: join(...noOpenParts, sig, OPT_OUT) },
    followupOpened: { wa: join(...openedParts, `— ${sender}`, OPT_OUT), emailSubject: `Noch eine Frage zum Bericht für ${f.name}?`, emailBody: join(...openedParts, sig, OPT_OUT) },
  };
}
